import { apiClient } from './client'
import { filenameFromHeaders } from './csvDownload'
import type { ApiResponse } from '@/types/api'

/**
 * 교재 진도 현황판 (한시) — docs/features/admin-textbook-progress-board/STATE.md
 */
export type PrintBookStatus = 'draft' | 'ready' | 'completed'

export interface TextbookProgressNextBook {
  bookId: number
  title: string
  volumeLabel: string | null
  materialType: string | null
  status: PrintBookStatus | string
}

export interface TextbookProgressBook {
  bookId: number
  title: string
  volumeLabel: string | null
  materialType: string
  status: PrintBookStatus | string
  totalLessons: number
  currentLessonRank: number
  remainingLessons: number
  /** null = 다음 권 미등록 */
  nextBook: TextbookProgressNextBook | null
}

export interface TextbookProgressEntitlement {
  level: string
  grantedLessons: number
  carriedOver: number
  usedLessons: number
  remainingLessons: number
  periodStart: string | null
  periodEnd: string | null
  graceUntil: string | null
  status: string
  source: string
  contentFeeGate: string | null
}

export interface TextbookProgressClassDay {
  /** mon / tue / wed / thu / fri / sat / sun */
  day: string
  /** HH:mm */
  startTime: string
  endTime: string
  durationMinutes: number
}

/** LC 클래스(timeslot) 시간표 */
export interface TextbookProgressClass {
  timeslotId: number
  name: string
  teacherName: string | null
  active: boolean
  days: TextbookProgressClassDay[]
}

/** 선두 위치 원생 1명 + 소속 클래스 */
export interface TextbookProgressLearner {
  userId: number
  fullName: string
  organizationId: number
  organizationName: string
  testCenter: boolean
  testEntitlement: boolean
  productName: string | null
  lastAccessedAt: string | null
  /** null = 클래스 미배정 */
  classSchedule: TextbookProgressClass | null
}

export interface TextbookProgressFrontrunner {
  userId: number
  fullName: string
  organizationId: number
  organizationName: string
  testCenter: boolean
  testEntitlement: boolean
  productName: string | null
  curriculumTitle: string | null
  tiedLearnerCount: number
  curriculumCompleted: boolean
  currentChapterTitle: string | null
  currentLessonTitle: string | null
  currentLessonRank: number | null
  courseLessonTotal: number
  /** ISO 8601 with offset */
  lastAccessedAt: string | null
  entitlement: TextbookProgressEntitlement | null
  checkupBook: TextbookProgressBook | null
  workbook: TextbookProgressBook | null
  /** 대표 원생의 소속 클래스. null = 미배정 */
  classSchedule: TextbookProgressClass | null
  /** 현재 레슨이 속한 실제 코스명(코어 코스군이면 CORE-A2 같은 분할 코스) */
  currentCourseName?: string | null
}

export interface TextbookProgressBoardRow {
  courseId: number
  courseName: string
  curriculumTitles: string[]
  learnerCount: number
  frontrunner: TextbookProgressFrontrunner | null
  /** 선두 위치(같은 rank) 원생 전원. 대표가 첫 번째. 선두 없으면 [] */
  frontrunnerLearners: TextbookProgressLearner[]
  /** 행에 묶인 실제 코스(커리큘럼 순서) */
  courseIds?: number[]
  /** 분할 코스를 이어 집계한 코어 코스군 행(CORE-A1/A2/A3 → Core A) */
  courseFamily?: boolean
}


export interface TextbookProgressBoard {
  generatedAt: string
  includeTest: boolean
  eligibleLearnerCount: number
  rows: TextbookProgressBoardRow[]
}

export interface TextbookProgressBoardParams {
  includeTest: boolean
}

const BASE = '/admin/textbook-progress-board'

export const textbookProgressBoardQueryKeys = {
  all: ['admin', 'textbook-progress-board'] as const,
  board: (params: TextbookProgressBoardParams) => ['admin', 'textbook-progress-board', params] as const,
}

export const textbookProgressBoardApi = {
  getBoard: async (params: TextbookProgressBoardParams): Promise<TextbookProgressBoard> => {
    const res = await apiClient.get<ApiResponse<TextbookProgressBoard>>(BASE, { params })
    const data = res.data.data
    if (!data) throw new Error('교재 진도 현황판 응답이 비어 있습니다.')
    return data
  },

  exportCsv: async (params: TextbookProgressBoardParams): Promise<{ blob: Blob; filename: string }> => {
    const res = await apiClient.get<Blob>(`${BASE}/export`, { params, responseType: 'blob' })
    return { blob: res.data, filename: filenameFromHeaders(res.headers, 'textbook-progress-board') }
  },
}

export interface LearnerProgressRow {
  userId: number
  fullName: string
  organizationId: number
  organizationName: string
  gradeLevel: string | null
  gender: string | null
  productName: string | null
  curriculumTitle: string | null
  courseName: string | null
  courseColorKey: 'core-a' | 'core-b' | 'core-c' | 'core-d' | 'core-e' | 'advanced' | 'elite' | null
  progressStatus: 'CURRENT' | 'COMPLETED' | 'UNAVAILABLE'
  chapterNumber: number | null
  lessonNumber: number | null
  chapterLessonCount: number | null
  chapterTitle: string | null
  lessonTitle: string | null
  lastAccessedAt: string | null
  testCenter: boolean
  testEntitlement: boolean
  progressGroupKey: string
  progressGroupName: string
}

export interface LearnerProgressResponse {
  generatedAt: string
  includeTest: boolean
  eligibleLearnerCount: number
  totalElements: number
  page: number
  size: number
  organizations: { id: number; name: string }[]
  curricula: { id: number; name: string }[]
  rows: LearnerProgressRow[]
  sort: LearnerProgressSort
}

export type LearnerProgressSort = 'PROGRESS' | 'CENTER_NAME'

export interface LearnerProgressParams {
  includeTest: boolean
  organizationId?: number
  curriculumId?: number
  q: string
  page: number
  size: number
  sort: LearnerProgressSort
}

export const learnerProgressApi = {
  list: async (params: LearnerProgressParams): Promise<LearnerProgressResponse> => {
    const res = await apiClient.get<ApiResponse<LearnerProgressResponse>>(`${BASE}/learners`, { params })
    if (!res.data.data) throw new Error('전체 원생 진도 응답이 비어 있습니다.')
    return res.data.data
  },
  exportCsv: async ({ page: _page, size: _size, ...params }: LearnerProgressParams): Promise<{ blob: Blob; filename: string }> => {
    const res = await apiClient.get<Blob>(`${BASE}/learners/export`, { params, responseType: 'blob' })
    return { blob: res.data, filename: filenameFromHeaders(res.headers, 'learner-progress') }
  },
}
